import React, { useState, useRef } from 'react'

// Plan Types
import { CloudBillingPlanFragment } from '@sketch/gql-types'

import {
  CardWrapper,
  Cents,
  Currency,
  PlanName,
  Price,
  Unit,
  Small,
  OuterCardWrapper,
  SmallText,
  StyledRadioButton,
  MobileCard,
  MobileRadioButton,
  MobilePlanName,
  MobilePlanContainer,
  MobileSmall,
  MobilePrice,
  MobileCurrency,
  MobileUnit,
} from './Card.styles'

import { Tooltip, useForDesktop, Flex } from '@sketch/components'

const READABLE_PLAN_NAME = {
  ESSENTIAL: 'Essential',
  TEAMS: 'Standard',
  BUSINESS: 'Business',
  STUDENT: 'Student',
} as const

// Helper functions
const calculatePrice = (value: number, output?: 'CENTS' | 'UNITS') => {
  const price = (value / 100).toFixed(2)
  const [units, cents] = `${price}`.split('.')

  if (output === 'CENTS') {
    return cents === '00' ? '' : cents
  } else if (output === 'UNITS') {
    return units
  }

  return price.replace(/\D00$/, '')
}

interface CardProps {
  currency?: {
    code: string
    symbol: string
  }
  isDisabled: boolean
  isSelected: boolean
  showAvailableOnlyOnYearlyNotice: boolean
  showAvailableOnlyOnYearlyTooltip: boolean
  onChange: (clickedPlan: CloudBillingPlanFragment) => void
  plan: CloudBillingPlanFragment
}

/*
 * Card
 *
 * Renders a single plan card with its info (plan name,
 * price per editor and monthly / yearly price).
 *
 * Cards are loaded from Apollo cache if available otherwise
 * default values are used.
 *
 */

const Card: React.FC<CardProps> = ({
  isDisabled,
  isSelected,
  onChange,
  plan,
  currency: selectedCurrency = {
    code: 'USD',
    symbol: '$',
  },
  showAvailableOnlyOnYearlyNotice = false,
  showAvailableOnlyOnYearlyTooltip = false,
}) => {
  const isDesktopAndBigger = useForDesktop()
  const radioRef = useRef<HTMLInputElement>(null)
  const [tooltipVisible, setTooltipVisible] = useState(false)
  const handleChange = () => onChange(plan)

  const planPrice = plan.prices.find(
    ({ currency }) => currency === selectedCurrency.code
  )

  const priceUnit = planPrice && calculatePrice(planPrice?.monthly, 'UNITS')
  const priceCents = planPrice && calculatePrice(planPrice?.monthly, 'CENTS')

  const name = READABLE_PLAN_NAME[plan.product] || plan.product

  if (!isDesktopAndBigger) {
    return (
      <MobileCard
        onClick={handleChange}
        isSelected={isSelected}
        tabIndex={-1}
        isDisabled={isDisabled}
        aria-disabled={isDisabled}
      >
        <MobileRadioButton
          data-testid={`${plan.id}-radio-button`}
          checked={isSelected}
          disabled={isDisabled}
          ref={radioRef}
          name="plan"
          onChange={handleChange}
          type="radio"
          value={plan.id}
        />

        <Flex alignItems="center" justifyContent="space-between">
          <MobilePlanContainer>
            <MobilePlanName isSelected={isSelected} disabled={isDisabled}>
              {name}
            </MobilePlanName>
            <MobileSmall isSelected={isSelected}>
              Monthly / per Editor
            </MobileSmall>
          </MobilePlanContainer>

          <MobilePrice>
            <MobileCurrency>{selectedCurrency.symbol}</MobileCurrency>
            <MobileUnit>{priceUnit}</MobileUnit>
            {priceCents && <Cents>{priceCents}</Cents>}
          </MobilePrice>
        </Flex>
      </MobileCard>
    )
  }

  const renderPlanPrice = ({ isSelected }: { isSelected: boolean }) => {
    return (
      <Price isSelected={isSelected}>
        <Currency>{selectedCurrency.symbol}</Currency>
        <Unit>{priceUnit}</Unit>
        {priceCents && <Cents>{priceCents}</Cents>}
      </Price>
    )
  }

  return (
    <OuterCardWrapper onMouseLeave={() => setTooltipVisible(false)}>
      <Tooltip
        placement="top"
        content="Available on yearly billing only"
        visible={tooltipVisible}
      >
        <CardWrapper
          isSelected={isSelected}
          tabIndex={-1}
          isDisabled={isDisabled}
          aria-disabled={isDisabled}
          // using a controlled Tooltip here because there's with the card disabled
          // events are not propagated leading to the Tooltip not hiding in same mouse situations
          onMouseEnter={() =>
            setTooltipVisible(showAvailableOnlyOnYearlyTooltip)
          }
          onMouseLeave={() => setTooltipVisible(false)}
        >
          <StyledRadioButton
            data-testid={`${plan.id}-radio-button`}
            checked={isSelected}
            disabled={isDisabled}
            name="plan"
            onChange={handleChange}
            type="radio"
            value={plan.id}
          />

          <PlanName>{name}</PlanName>
          {renderPlanPrice({ isSelected })}
          <Small isSelected={isSelected}>Monthly / per Editor</Small>
        </CardWrapper>
      </Tooltip>
      {showAvailableOnlyOnYearlyNotice && (
        <SmallText>
          Available on <strong>yearly billing only</strong>
        </SmallText>
      )}
    </OuterCardWrapper>
  )
}

export default Card
